// @ts-strict-ignore
import { FormErrors } from 'types'
import { useMemo } from 'react'
import { useForm, useFormValidator, useMutationBasedFormSubmitHandlers } from 'hooks/form'
import { graphql, useFragment } from 'react-relay'
import { useCodeTestBrowserForm_SubmitProjectSubmissionMutation } from '__generated__/useCodeTestBrowserForm_SubmitProjectSubmissionMutation.graphql'
import { useCodeTestBrowserForm_Project$key } from '__generated__/useCodeTestBrowserForm_Project.graphql'

interface ICodeTestFormFields {
  title: string
  url: string
  status: string
  screenshotUrl: string
  description: string
  projectId?: string
}

function validate(values: ICodeTestFormFields) {
  const errors: FormErrors<ICodeTestFormFields> = {}

  if (!values.title) {
    errors.title = 'Required'
  }

  if (!values.url) {
    errors.url = 'Required'
  } else {
    // Perform very basic url validation.
    let url = values.url.trim()

    if (url.indexOf(' ') !== -1 || url.indexOf('.') === -1) {
      errors.url = 'URL does not appear to be valid'
    }
  }

  if (values.description && values.description.length > 1000) {
    errors.url = 'Limit is 1000 characters'
  }

  return errors
}

function useDefaultValues(): ICodeTestFormFields {
  return useMemo(() => ({
    title: '',
    url: '',
    status: '',
    screenshotUrl: '',
    description: '',
  }), [])
}

export function useCodeTestBrowserForm(onClose: () => void, projectFragmentRef?: useCodeTestBrowserForm_Project$key) {
  const project = useFragment(graphql`
    fragment useCodeTestBrowserForm_Project on Project {
      id
      title
      status
    }
  `, projectFragmentRef)

  return useForm<ICodeTestFormFields, useCodeTestBrowserForm_SubmitProjectSubmissionMutation>({
    resolver: useFormValidator(validate),
    submitHandlers: useMutationBasedFormSubmitHandlers(graphql`
      mutation useCodeTestBrowserForm_SubmitProjectSubmissionMutation($input: CreateProjectSubmissionInput!) {
        createProjectSubmission(input: $input) {
          projectSubmission {
            id
          }
        }
      }
    `, values => ({
      input: {
        projectId: values?.projectId,
        status: project ? project?.status as any : 'public',
        url: values?.url,
        screenshotUrl: values?.screenshotUrl,
        description: values?.description,
        projectTitle: project ? project?.title : values?.title,
      },
    }), {
      successMessage: 'Code submitted',
      verifySuccess(result) {
        return Boolean(result?.createProjectSubmission?.projectSubmission)
      },
      onSuccess() {
        onClose()
      },
    }),
    defaultValues: useDefaultValues(),
  })
}
